import { useState } from 'react';
import { isDocumentNode } from '@apollo/client/utilities';
import { DocumentNode, OperationDefinitionNode } from 'graphql';
import { firstValueFrom } from 'rxjs';
import { GQL } from '@pass-frontend/data-access/state-management/apollo';
import {
  eventBus,
  useWhileMounted,
  TBusItem,
} from '@pass-frontend/utility/event-bus';

/** Returns a boolean which is `true` in between the request/complete events of a given GraphQL query.
 * Expects the `queryOrName` to be a string or a parsed query, which one should not change between calls.
 */
export function useLoadingState(queryOrName: string | DocumentNode): boolean {
  const [isLoading, setLoading] = useState(false);

  const opName = isDocumentNode(queryOrName)
    ? (queryOrName.definitions[0] as OperationDefinitionNode).name?.value
    : queryOrName;

  const startEvent = (e: TBusItem) =>
    GQL.request.match(e) && e.payload.operation === opName;

  const finishEvent = (e: TBusItem) =>
    (GQL.complete.match(e) || GQL.error.match(e)) &&
    e.payload.operation === opName;

  useWhileMounted(() =>
    eventBus.listenBlocking(
      startEvent,
      () => {
        setLoading(true);
        return firstValueFrom(eventBus.query(finishEvent));
      },
      {
        complete: () => setLoading(false),
      },
    ),
  );

  return isLoading;
}
